import { ReactNode, Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import {
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
} from "@carbon/react";
import Slide from "../../../components/UI/Slide/Slide";
import { FileReader, TagBadge } from "../../../components";
import {
  PurchaseRequestItemType,
  PurchaseRequestItemV2,
} from "../../../types";
import {
  IT_DOESNT_HAVE_SPEC,
  TO_BE_AGREED,
  formatDate,
} from "../../../utils";
import AssignedBuyerField from "./AssignedBuyerField";

export interface DetailItemInfoPanelProps {
  /** The request the item belongs to: scopes the buyer picker. */
  requestId?: string;
  item: PurchaseRequestItemV2 | null;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  langEs: boolean;
}

/**
 * The detail screen's item "material info" slide-over (Task 5) — Carbon `StructuredList` inside the
 * shared `Slide`, in place of the pre-Carbon Tailwind `<dl>`. Distinct from the list screen's
 * `PurchaseRequestItemInfoPanel` because this one also surfaces the item-level fields the pre-Carbon
 * detail panel showed (SERVICE purpose/scope, manual delivery address, needed dates, requesting
 * site) alongside the material fields. Every field, and the order it renders in, is unchanged.
 */
const DetailItemInfoPanel = ({
  requestId,
  item,
  open,
  setOpen,
  langEs,
}: DetailItemInfoPanelProps) => {
  const { t } = useTranslation();
  const material = item?.material;

  const row = (label: string, value: ReactNode) => (
    <StructuredListRow key={label}>
      <StructuredListCell className="pr-detail__info-label">
        {label}
      </StructuredListCell>
      <StructuredListCell>{value}</StructuredListCell>
    </StructuredListRow>
  );

  const isService = item?.itemType === PurchaseRequestItemType.SERVICE;

  return (
    <Slide open={open} setOpen={setOpen}>
      {item ? (
        <div className="pr-detail__info-panel">
          <h3 className="pr-detail__info-title">
            {t("purchaseRequests.items.materialInfo")}
          </h3>
          <StructuredListWrapper>
            <StructuredListBody>
              {row(
                t("purchaseRequests.items.columns.material"),
                `${material?.ean ? `${material.ean} — ` : ""}${
                  material?.globalMaterialName || "-"
                }`
              )}
              {item?.description
                ? row(
                    t("purchaseRequests.items.itemText"),
                    <span className="pr-detail__info-preformatted">
                      {item.description}
                    </span>
                  )
                : null}
              {row(
                t("purchaseRequests.filters.assignedBuyer"),
                <AssignedBuyerField requestId={requestId} item={item} />
              )}
              {isService && item?.purpose
                ? row(t("purchaseRequests.items.columns.purpose"), item.purpose)
                : null}
              {isService && item?.brand
                ? row(
                    t("purchaseRequests.items.columns.application"),
                    item.brand
                  )
                : null}
              {row(
                t("material.merchancyCode"),
                langEs ? material?.category?.name : material?.category?.nameEn
              )}
              {/* A service's material has no unit: the item carries its own. */}
              {material?.measurementUnit || item.measurementUnit
                ? row(
                    t("material.measurementUnit"),
                    material?.measurementUnit || item.measurementUnit
                  )
                : null}
              {material?.deliveryFormat
                ? row(t("material.deliveryFormat"), material.deliveryFormat)
                : null}
              {row(
                t("material.deliveryPlace"),
                material?.deliveryPlace?.name ?? TO_BE_AGREED
              )}
              {item?.address
                ? row(
                    t("purchaseRequests.items.columns.deliveryAddress"),
                    item.address
                  )
                : null}
              {item?.neededFrom || item?.neededTo
                ? row(
                    t("purchaseRequests.items.columns.needed"),
                    <>
                      {item?.neededFrom && formatDate(item.neededFrom)}
                      {item?.neededTo &&
                        item.neededTo !== item.neededFrom &&
                        ` - ${formatDate(item.neededTo)}`}
                    </>
                  )
                : null}
              {item?.requestingSite
                ? row(
                    t("purchaseRequests.items.columns.requestingCenter"),
                    `${item.requestingSite.internalCode ?? ""}${
                      item.requestingSite.name
                        ? ` — ${item.requestingSite.name}`
                        : ""
                    }`
                  )
                : null}
              {material?.certifications && material.certifications.length > 0
                ? row(
                    t("material.certifications"),
                    material.certifications.map((cert) => cert.name).join(", ")
                  )
                : null}
              {material?.tags && material.tags.length > 0
                ? row(
                    t("tags.tags", { defaultValue: "Tags" }),
                    <div className="pr-detail__info-tags">
                      {material.tags.map((tag) => (
                        <TagBadge tag={tag} size="sm" key={tag.id} />
                      ))}
                    </div>
                  )
                : null}
              {material?.brand
                ? row(
                    t("material.brand"),
                    <span className="pr-detail__info-preformatted">
                      {material.brand}
                    </span>
                  )
                : null}
              {material?.comments
                ? row(
                    t("material.comments"),
                    <span className="pr-detail__info-preformatted">
                      {material.comments}
                    </span>
                  )
                : null}
              {material?.details
                ? row(
                    t("newMaterialForm.details"),
                    <span className="pr-detail__info-preformatted">
                      {material.details}
                    </span>
                  )
                : null}
              {row(
                t("material.confidentMaterial"),
                material?.confident ? t("cta.yes") : t("cta.no")
              )}
              {row(
                t("material.confidential"),
                material?.confidentSpec ? t("cta.yes") : t("cta.no")
              )}
              {row(
                t("material.specification"),
                material?.fileURL ? (
                  <FileReader fileKey={material.fileURL} />
                ) : (
                  IT_DOESNT_HAVE_SPEC
                )
              )}
              {material?.owner?.fullName
                ? row(t("material.responsible"), material.owner.fullName)
                : null}
              {material?.user?.fullName
                ? row(t("material.owner"), material.user.fullName)
                : null}
            </StructuredListBody>
          </StructuredListWrapper>
        </div>
      ) : null}
    </Slide>
  );
};

export default DetailItemInfoPanel;
